'use client';

import Image from 'next/image';
import Link from 'next/link';
import { HeaderButton } from '../_components/SignInButton';
import { usePathname } from 'next/navigation';
import { HomeIcon, LightbulbIcon, UserIcon, MenuIcon, XIcon, VideoIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect, useContext } from 'react';
import DashboardNav from './DashboardNav';
import { useSession } from 'next-auth/react';
import { useAccountList } from '../lib/user';
import { useStatsLogList } from '../lib/content';
import { AppStateContext } from '../_components/Providers';

export default function DashboardLayout({ children }: { children: any }) {
  const session = useSession();
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { platformFilter, setPlatformFilter } = useContext(AppStateContext);

  const { accounts } = useAccountList({});
  const { statsLogs } = useStatsLogList(platformFilter, {});
  const reverseTikTokStatsLogs = statsLogs.slice();
  reverseTikTokStatsLogs.reverse();
  const latestStats = statsLogs.find(Boolean);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMobileMenuOpen && !target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  const navigationItems = [
    {
      href: '/dashboard',
      label: 'Dashboard',
      icon: HomeIcon,
      active: pathname === '/dashboard',
    },
    {
      href: '/dashboard/videos',
      label: 'Videos',
      icon: VideoIcon,
      active: pathname.startsWith('/dashboard/videos'),
    },
    {
      href: '/dashboard/ideas',
      label: 'Ideas',
      icon: LightbulbIcon,
      active: pathname.startsWith('/dashboard/ideas'),
    },
    {
      href: '/dashboard/profile',
      label: 'Profile',
      icon: UserIcon,
      active: pathname === '/dashboard/profile',
    },
    // Future navigation items can be easily added here
    // {
    //   href: '/dashboard/analytics',
    //   label: 'Analytics',
    //   icon: BarChart3Icon,
    //   active: pathname.startsWith('/dashboard/analytics'),
    // },
    // {
    //   href: '/dashboard/calendar',
    //   label: 'Calendar',
    //   icon: CalendarIcon,
    //   active: pathname.startsWith('/dashboard/calendar'),
    // },
    // {
    //   href: '/dashboard/content',
    //   label: 'Content',
    //   icon: BookOpenIcon,
    //   active: pathname.startsWith('/dashboard/content'),
    // },
    // {
    //   href: '/dashboard/settings',
    //   label: 'Settings',
    //   icon: SettingsIcon,
    //   active: pathname.startsWith('/dashboard/settings'),
    // },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/5 backdrop-blur-md border-b border-white/10">
        <div className="mx-auto px-4 sm:px-6 py-4">
          <nav className="flex justify-between items-center">
            {/* Logo */}
            <Link
              href="/dashboard"
              className="group flex items-center gap-3 hover:scale-105 transition-transform duration-200"
            >
              <div className="relative">
                <Image
                  width={1024 / 20}
                  height={1024 / 20}
                  src="/ignission-logo.png"
                  alt="Ignission logo"
                  className="rounded-full"
                />
              </div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  ignission
                </h1>
              </div>
            </Link>

            {/* Desktop Navigation */}
            {session.status === 'authenticated' && (
              <DashboardNav navigationItems={navigationItems} pathname={pathname} />
            )}

            {/* User Actions */}
            <div className="flex items-center gap-3">
              {session.status === 'authenticated' && (
                <>
                  <HeaderButton
                    accounts={accounts}
                    platformFilter={platformFilter}
                    setPlatformFilter={setPlatformFilter}
                  />
                  {/* Mobile Menu Button */}
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="lg:hidden p-2 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-200"
                  >
                    {isMobileMenuOpen ? (
                      <XIcon className="w-5 h-5 text-white" />
                    ) : (
                      <MenuIcon className="w-5 h-5 text-white" />
                    )}
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>

        {/* Mobile Navigation Menu */}
        {latestStats && isMobileMenuOpen && (
          <div className="mobile-menu-container lg:hidden border-t border-white/10 bg-white/5 backdrop-blur-md">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map(item => {
                const IconComponent = item.icon;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                      item.active
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white hover:bg-white/10'
                    )}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="relative z-10 transition-all">{children}</main>
    </div>
  );
}
